import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { CenteredSpinner } from '../../components/ui/Spinner';
import {
  previewCardChannelText,
  type MyPreviewsDto,
  type MyPreviewsResponse,
} from './myPreviewsDto';

// Ad Preview Stage 3 multi-location amendment §A4. Public contact-
// summary page rendered on /my-previews/:token (outside SignedInGuard
// + AppShell -- see src/App.tsx). A franchisee who owns N locations
// receives ONE SMS with this URL; the page lists one card per location
// linking through to the individual /preview/:token page.
//
// Data fetch uses plain `fetch` (no Authorization header -- franchisees
// aren't signed in). Lifecycle states (not_found / revoked / expired)
// map to dedicated friendly surfaces, mirroring PreviewPage.

type FetchState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ok'; dto: MyPreviewsDto };

async function fetchMyPreviews(token: string): Promise<FetchState> {
  try {
    const res = await fetch(
      `/api/public/preview-contact/${encodeURIComponent(token)}`,
      { credentials: 'omit' },
    );
    if (res.status === 429) {
      return {
        kind: 'error',
        message: 'Too many requests right now. Please wait a minute and refresh.',
      };
    }
    const body = (await res.json().catch(() => ({}))) as Partial<MyPreviewsResponse>;
    if (body && body.status === 'ok') {
      return { kind: 'ok', dto: body as MyPreviewsDto };
    }
    if (body && body.status === 'revoked') {
      return {
        kind: 'error',
        message: 'These previews have been revoked. Please contact your agency.',
      };
    }
    if (body && body.status === 'expired') {
      const exp = (body as { expiresAt?: string }).expiresAt;
      return {
        kind: 'error',
        message: exp
          ? `These previews expired on ${new Date(exp).toLocaleDateString()}. Contact your agency for an updated version.`
          : 'These previews have expired.',
      };
    }
    if (body && body.status === 'not_found') {
      return { kind: 'error', message: 'Previews not found.' };
    }
    return { kind: 'error', message: 'Could not load previews.' };
  } catch {
    return {
      kind: 'error',
      message: 'Could not load previews. Check your connection and try again.',
    };
  }
}

export function MyPreviewsPage() {
  const { token } = useParams<{ token: string }>();
  const [state, setState] = useState<FetchState>({ kind: 'loading' });

  useEffect(() => {
    if (!token) {
      setState({ kind: 'error', message: 'Previews not found.' });
      return;
    }
    let cancelled = false;
    setState({ kind: 'loading' });
    fetchMyPreviews(token).then((s) => {
      if (!cancelled) setState(s);
    });
    return () => {
      cancelled = true;
    };
  }, [token]);

  useEffect(() => {
    if (state.kind === 'ok' && state.dto.brandName) {
      document.title = `Ad Previews — ${state.dto.brandName}`;
    } else {
      document.title = 'Ad Previews';
    }
  }, [state]);

  if (state.kind === 'loading') {
    return (
      <div className="min-h-screen bg-white">
        <div className="flex items-center justify-center pt-32">
          <CenteredSpinner />
        </div>
      </div>
    );
  }
  if (state.kind === 'error') {
    return <ErrorShell message={state.message} />;
  }
  return <MyPreviewsLoaded dto={state.dto} />;
}

function ErrorShell({ message }: { message: string }) {
  return (
    <div className="min-h-screen bg-white text-slate-900">
      <div className="max-w-md mx-auto pt-24 px-4 text-center space-y-3">
        <h1 className="text-2xl font-semibold">Previews unavailable</h1>
        <p className="text-slate-600">{message}</p>
        <p className="text-xs text-slate-400 pt-6">Kyck</p>
      </div>
    </div>
  );
}

function MyPreviewsLoaded({ dto }: { dto: MyPreviewsDto }) {
  const count = dto.previews.length;
  return (
    <div className="min-h-screen bg-white text-slate-900">
      <MyPreviewsHeader dto={dto} />
      <main className="max-w-2xl mx-auto px-4 pb-24 pt-6">
        {count === 0 ? (
          <p className="text-sm text-slate-500 italic py-8 text-center">
            These previews are no longer available. Contact your agency for an
            updated version.
          </p>
        ) : (
          <>
            <p className="text-sm text-slate-500 mb-4">
              {count} {count === 1 ? 'preview' : 'previews'} available:
            </p>
            <ul className="space-y-3">
              {dto.previews.map((p) => (
                <li key={p.previewToken}>
                  <a
                    href={`/preview/${p.previewToken}`}
                    className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 px-4 py-4 transition-colors hover:border-slate-400 hover:bg-slate-50 active:bg-slate-100"
                  >
                    <span className="min-w-0">
                      <span className="block font-medium text-slate-900 truncate">
                        {p.locationDisplayName}
                      </span>
                      <span className="block text-xs text-slate-500">
                        {previewCardChannelText(p.channels)}
                      </span>
                    </span>
                    <span className="shrink-0 text-sm font-medium text-slate-700">
                      View Preview →
                    </span>
                  </a>
                </li>
              ))}
            </ul>
            <p className="mt-6 text-center text-xs text-slate-400">
              All previews expire{' '}
              {new Date(dto.expiresAt).toLocaleDateString(undefined, {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
              })}
            </p>
          </>
        )}
      </main>
      <footer className="border-t border-slate-200 py-6 text-center text-xs text-slate-400">
        Created by Kyck
      </footer>
    </div>
  );
}

function MyPreviewsHeader({ dto }: { dto: MyPreviewsDto }) {
  // §A4.2 header: mockup banner, "Your ad previews", brand + campaign
  // context, then the contact's name. Each line is omitted when its
  // value is empty so a deleted contact / unnamed brand still renders
  // cleanly.
  const subtitle = [dto.brandName, dto.campaignName]
    .filter((s) => s.trim() !== '')
    .join(' — ');
  return (
    <header className="border-b border-slate-200 bg-slate-50">
      <div className="max-w-2xl mx-auto px-4 py-5 space-y-1">
        <p className="text-xs uppercase tracking-wide text-amber-700">
          Ad Preview — not yet published
        </p>
        <h1 className="text-2xl font-semibold text-slate-900">Your ad previews</h1>
        {subtitle && <p className="text-sm text-slate-600">{subtitle}</p>}
        {dto.contactName && (
          <p className="text-sm text-slate-500">{dto.contactName}</p>
        )}
      </div>
    </header>
  );
}
